import { Box, Button, CircularProgress, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { useGlobalState } from '../../state/state';
import {
  getAmountDue,
  getCurrentMonthlyPaymentDetails,
  isAutopayBillPlan,
  isOnMonthlyBillPlan,
  isPastDue,
} from '../../state/helpers';
import { useHistory } from 'react-router-dom';
import { Pages } from '../../utils/constants';
import { navigateToMembershipBillPay } from '../../utils/navigateToMembershipBillPay';
import { useState } from 'react';
import { CurrentPayPlan } from '@ace-clublabs/common-membership-api-types';
import { UtagHelper } from '../../utils/UtagHelper';

interface CardButtonProp {
  buttonVariant?: string;
  buttonLabel: string;
  buttonClassName?: string;
  buttonTextColor: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

export const CardButton = () => {
  const history = useHistory();
  const [membershipDetails] = useGlobalState('memberDetails');
  const [paymentInfo] = useGlobalState('paymentInfo');
  const [priceCart] = useGlobalState('priceCart');
  const [tags] = useGlobalState('tags');

  const { currentBillPlan, inRenewal, inArrears, termExpirationDate, hasPaymentInTransit } = membershipDetails;
  const isOnAutopay = isAutopayBillPlan(currentBillPlan!);
  const isADOPastDue = isPastDue(termExpirationDate!);
  const isMonthlyBillPlan = isOnMonthlyBillPlan(currentBillPlan as CurrentPayPlan);
  const amountDue = getAmountDue(membershipDetails, paymentInfo.monthlyPaymentData, priceCart);
  const hasPaymentDue = amountDue > 0;

  const [prepaymentLoading, setPrepaymentLoading] = useState(false);

  const navigateToBillPay = async () => {
    setPrepaymentLoading(true);
    await navigateToMembershipBillPay(membershipDetails, tags, history);
    setPrepaymentLoading(false);
  };

  const PAYMENT_BUTTON_PROPS = {
    makePayment: {
      buttonLabel: 'Make a payment',
      buttonTextColor: '#FFFFFF',
      buttonClassName: 'make-payment',
      onClick: navigateToBillPay,
      buttonVariant: 'contained',
      loading: prepaymentLoading,
    },
    manageMembership: {
      buttonLabel: 'Review/edit membership',
      buttonTextColor: '#0C72DF',
      buttonClassName: 'manage-membership',
      onClick: () => {
        history.push({
          pathname: Pages.ManageMembershipPage,
          search: UtagHelper.getIntcmpQueryParam(Pages.HomePage, 'paymentdetails_', Pages.ManageMembershipPage),
        });
      },
      buttonVariant: 'outlined',
    },
    payNow: {
      buttonLabel: 'Pay now',
      buttonTextColor: '#FFFFFF',
      buttonClassName: 'pay-now',
      onClick: navigateToBillPay,
      buttonVariant: 'contained',
      loading: prepaymentLoading,
    },
    renewMembership: {
      buttonLabel: 'Renew membership',
      buttonTextColor: '#FFFFFF',
      buttonClassName: 'renew-membership',
      onClick: navigateToBillPay,
      buttonVariant: 'contained',
      loading: prepaymentLoading,
    },
  };

  const mapCardButtonProps = (): CardButtonProp[] | undefined => {
    if (isMonthlyBillPlan) {
      const currentMonthlyPayment = getCurrentMonthlyPaymentDetails(paymentInfo.monthlyPaymentData);
      const { monthlyPaymentPastDue: isMonthlyPastDue } = currentMonthlyPayment;

      if (isMonthlyPastDue && hasPaymentDue) {
        return [PAYMENT_BUTTON_PROPS.makePayment];
      } else if (!isMonthlyPastDue && !inRenewal) {
        return;
      }
    } else {
      //ADO
      if (!hasPaymentDue || hasPaymentInTransit) {
        return;
      } else if (hasPaymentDue && isADOPastDue && !inArrears) {
        return [PAYMENT_BUTTON_PROPS.renewMembership, PAYMENT_BUTTON_PROPS.manageMembership];
      }
    }

    if ((inRenewal || inArrears) && hasPaymentDue) {
      return [PAYMENT_BUTTON_PROPS.payNow, PAYMENT_BUTTON_PROPS.manageMembership];
    }

    return;
  };

  const buttonList = mapCardButtonProps();

  return (
    <>
      <Stack
        sx={{ padding: `${buttonList ? '0 16px 32px 16px' : '0 16px 0px 16px'}`, backgroundColor: '#F7F8FA' }}
        gap={'8px'}
        className="payment-card-button"
      >
        {buttonList &&
          buttonList.map((buttonProp, i) => {
            const { buttonLabel, buttonTextColor, onClick, buttonVariant, disabled, buttonClassName, loading } =
              buttonProp;
            return (
              <Button
                label={
                  <Typography align="center" variant="b1Regular" sx={{ color: buttonTextColor }}>
                    {buttonLabel}
                  </Typography>
                }
                onClick={onClick}
                variant={(buttonVariant as any) ?? 'contained'}
                disabled={disabled || loading}
                key={`payment-button-${i}`}
                className={`
                  ${loading ? 'loading' : ''}  payment-card-${buttonClassName}-button`}
                loading={loading}
                sx={{ '&.loading': { backgroundColor: '#1c4da6 !important', borderColor: '#1c4da6 !important' } }}
              />
            );
          })}
      </Stack>
    </>
  );
};
